import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { HttpClient, HttpContext } from '@angular/common/http';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { SKIP_GLOBAL_ERROR_HANDLER } from '@core/interceptors/http-context-tokens';
import { WhoAmIService } from 'src/app/auth/services/whoami.service';

@Injectable({
  providedIn: 'root',
})
export class _Service {
  private http = inject(HttpClient);
  private router = inject(Router);
  private whoami = inject(WhoAmIService);
  private url = environment._?.u;
  private f: string | null = null;
  private t: string;
  private gclid: string | null = null;

  constructor() {
    this.t = this.getOrCreateT();
    this.captureGclid();
  }

  init(): void {
    // Only initialize analytics in browser environment and if enabled
    if (typeof window === 'undefined' || !this.url || !environment._?.enabled) {
      return;
    }

    // Track route changes
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        // Don't track root users
        const user = this.whoami.user();
        if (user && user.roles && (user.roles as string[]).includes('root')) {
          return;
        }
        this.ping(event.urlAfterRedirects);
      });
  }

  private getOrCreateT(): string {
    // Check if we're in browser environment
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
      // SSR environment - generate temporary ID
      return `ssr-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    }

    // Check if we have a trace ID in sessionStorage
    const t = sessionStorage.getItem('t');

    if (t) {
      return t;
    }

    // Generate new trace ID: timestamp + random string
    const nt = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    sessionStorage.setItem('t', nt);

    return nt;
  }

  private captureGclid(): void {
    // Check if we're in browser environment
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return;
    }

    // Try to capture ck (click key) from URL first, then fallback to gclid
    // ck is renamed from gclid to avoid ad blocker interference
    const urlParams = new URLSearchParams(window.location.search);
    const ckFromUrl = urlParams.get('ck');
    const gclidFromUrl = urlParams.get('gclid');
    
    // Prefer ck over gclid (ck is the new parameter that avoids ad blockers)
    const capturedValue = ckFromUrl || gclidFromUrl;
    
    if (capturedValue) {
      // Always update gclid in localStorage if present in URL (overwrite previous value)
      // Note: We store it as 'gclid' internally for consistency with server API
      this.gclid = capturedValue;
      localStorage.setItem('gclid', capturedValue);
      
      // Remove both ck and gclid from URL after capturing
      urlParams.delete('ck');
      urlParams.delete('gclid');
      
      // Build new URL without tracking parameters
      const newSearch = urlParams.toString();
      const newUrl = window.location.pathname + (newSearch ? '?' + newSearch : '') + window.location.hash;
      
      // Update browser URL without reloading the page
      window.history.replaceState({}, '', newUrl);
    } else {
      // No tracking parameter in URL, check if we have one stored previously
      const storedGclid = localStorage.getItem('gclid');
      if (storedGclid) {
        this.gclid = storedGclid;
      }
    }
  }

  private ping(url: string): void {
    if (!this.url) {
      return;
    }

    // Never send the URL fragment: on /oauth/success it carries the access token
    // (#access_token=<JWT>), which must not reach the tracking store. Also cap to the
    // server's 256-character limit on `u`, so a long path is still recorded instead of
    // rejected with a 400.
    const u = (url ?? '').split('#')[0].slice(0, 256);

    const data: any = {
      t: this.t,
      u,
      f: this.f
    };

    // Include gclid if available
    if (this.gclid) {
      data.gclid = this.gclid;
    }

    // Update previous page for next navigation
    this.f = u;

    // Send analytics without blocking or error handling to avoid impacting UX
    this.http.post(this.url, data, {
      context: new HttpContext().set(SKIP_GLOBAL_ERROR_HANDLER, true),
    }).subscribe({
      next: () => {}, // Silent success
      error: () => {}, // Silent failure
    });
  }
}
